import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from "react-query";
import CustomerApiService from "../../../CustomerApiService";

/**
 * FR-person -> destination-field grouping API calls, backing the Facial Recognition tab
 * (`../facialRecognition/FrScreen.tsx`). Mirrors `../fields/service.ts`'s convention: thin
 * wrappers over `CustomerApiService.fetch`, bare-JSON contract, one function per endpoint. Types
 * here mirror `apps/customer-api/src/routes/spotter/frMapping.ts`'s wire shapes verbatim — see
 * that file's doc comment for the full read story (dual-purpose `GET`, the additive
 * `to_view_id`/`scope` fields beyond the pipeline's own documented contract).
 */

const PREFIX = "/spotter";

/** One named (`USER_CONFIRMED`) FR person — the grouping UI's multi-select source. */
export interface FrPerson {
  id: string;
  name: string;
}

/** `GET /fr-persons` — live from iconik, admin-UI only. */
export async function getFrPersons(): Promise<FrPerson[]> {
  return CustomerApiService.fetch<FrPerson[]>("GET", `${PREFIX}/fr-persons`);
}

/** One person's CURRENT assignment, as returned by `GET`/echoed by `../fields/service.ts`'s
 *  `saveFields`. */
export interface FrMappingWireEntry {
  person_id: string;
  person_label: string;
  destination_field: string;
  field_type?: string;
  to_view_id?: string;
  scope?: "asset" | "segment";
}

export interface StoredFrMapping {
  entries: FrMappingWireEntry[];
}

/** GET `/fr-mapping` — every currently-assigned person. */
export async function getFrMapping(): Promise<StoredFrMapping> {
  return CustomerApiService.fetch<StoredFrMapping>(
    "GET",
    `${PREFIX}/fr-mapping`
  );
}

/** One entry as SUBMITTED on `saveFrMapping`. */
export interface FrMappingPutEntry {
  person_id: string;
  person_label: string;
  to_view_id?: string;
  to_field_id?: string;
}

/** PUT `/fr-mapping` — persist the full current person-assignment set. Full replace-by-diff at
 *  the storage layer (see the backend route's doc comment) — the caller always submits every
 *  currently configured assignment, same convention `../fields/service.ts`'s `saveFields` uses. */
export async function saveFrMapping(
  entries: FrMappingPutEntry[]
): Promise<StoredFrMapping> {
  return CustomerApiService.fetch<StoredFrMapping>(
    "PUT",
    `${PREFIX}/fr-mapping`,
    { entries }
  );
}

export const FR_PERSONS_QUERY_KEY = "spotter-fr-persons";
export const FR_MAPPING_QUERY_KEY = "spotter-fr-mapping";

export function useFrPersons(): UseQueryResult<FrPerson[], Error> {
  return useQuery<FrPerson[], Error>(FR_PERSONS_QUERY_KEY, getFrPersons);
}

export function useFrMapping(): UseQueryResult<StoredFrMapping, Error> {
  return useQuery<StoredFrMapping, Error>(FR_MAPPING_QUERY_KEY, getFrMapping);
}

export function useSaveFrMapping(): UseMutationResult<
  StoredFrMapping,
  Error,
  FrMappingPutEntry[]
> {
  return useMutation(saveFrMapping);
}
